/**
 * Webview Bridge Service
 *
 * Core service for bidirectional communication between web app and native webview.
 * Handles sending messages to native and receiving messages from native.
 */

import {
  type ListenerOptions,
  type MessageDirection,
  type MessageHandler,
  type SendMessageOptions,
  type WebviewBridgeConfig,
  type WebviewMessage,
  type WebviewResponse,
} from './webview-bridge.type';

const isBrowser = globalThis.document !== undefined;

/**
 * Coerce an unknown value to a string if it is a string or a number.
 * Returns undefined for nullish / object values so we can fall back cleanly
 * instead of producing '[object Object]'.
 */
const coerceToString = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
};

/**
 * Normalize an error field coming from native. Strings pass through; objects
 * get JSON-serialized so we don't leak '[object Object]' into logs.
 */
const normalizeErrorField = (value: unknown): string | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return undefined;
  }
};

/**
 * Resolve a sane default for `allowedOrigin`.
 * Prefer the current document's origin so the incoming-message check is real.
 * Fall back to `'*'` only when no location is available (SSR / Node).
 */
const resolveDefaultAllowedOrigin = (): string => globalThis.location?.origin ?? '*';

/**
 * Default configuration for webview bridge
 */
const DEFAULT_MESSAGE_PREFIX = 'WEBVIEW';

/**
 * Webview Bridge Service Class
 *
 * Manages communication with native webview container using postMessage API.
 */
export class WebviewBridgeService {
  private readonly config: Required<WebviewBridgeConfig>;
  private readonly handlers: Map<string, Set<MessageHandler>> = new Map();
  private readonly pendingRequests: Map<
    string,
    {
      resolve: (value: WebviewResponse<Record<string, unknown>>) => void;
      reject: (error: Error) => void;
      timeout: ReturnType<typeof setTimeout>;
    }
  > = new Map();
  private isInitialized = false;
  private boundHandleIncomingMessage: ((event: MessageEvent) => void) | null = null;

  constructor(config: WebviewBridgeConfig = {}) {
    this.config = {
      allowedOrigin: config.allowedOrigin ?? resolveDefaultAllowedOrigin(),
      messagePrefix: config.messagePrefix ?? DEFAULT_MESSAGE_PREFIX,
    };
  }

  /**
   * Initialize the webview bridge service
   * Sets up message listener for receiving messages from native
   */
  public initialize(): void {
    if (!isBrowser) {
      return;
    }

    if (this.isInitialized) {
      return;
    }

    this.boundHandleIncomingMessage = this.handleIncomingMessage.bind(this);
    globalThis.addEventListener('message', this.boundHandleIncomingMessage);
    this.isInitialized = true;
  }

  /**
   * Cleanup and remove event listeners
   */
  public destroy(): void {
    if (!isBrowser) {
      return;
    }

    if (this.boundHandleIncomingMessage) {
      globalThis.removeEventListener('message', this.boundHandleIncomingMessage);
      this.boundHandleIncomingMessage = null;
    }
    this.handlers.clear();

    // Reject all pending requests
    this.pendingRequests.forEach(({ reject, timeout }) => {
      clearTimeout(timeout);
      reject(new Error('Webview bridge destroyed'));
    });
    this.pendingRequests.clear();

    this.isInitialized = false;
  }

  /**
   * Send a message to the native webview
   *
   * @param type - Message type/event name
   * @param data - Message payload
   * @param options - Send message options
   * @returns Promise that resolves with response if waitForResponse is true
   */
  public async send<T = Record<string, unknown>, R = Record<string, unknown>>(
    type: string,
    data: T,
    options: SendMessageOptions = {}
  ): Promise<WebviewResponse<R> | undefined> {
    if (!isBrowser) {
      throw new Error('Webview bridge can only be used in browser environment');
    }

    if (!this.isInitialized) {
      this.initialize();
    }

    const messageId = this.generateMessageId();
    const messageType = `${this.config.messagePrefix}:${type}`;

    const message: WebviewMessage<T> = {
      id: messageId,
      type: messageType,
      data,
      timestamp: Date.now(),
    };

    if (globalThis.ReactNativeWebView && typeof globalThis.ReactNativeWebView.postMessage === 'function') {
      globalThis.ReactNativeWebView.postMessage(JSON.stringify(message));
    } else if (globalThis.webkit?.messageHandlers?.webview) {
      // iOS WKWebView
      globalThis.webkit.messageHandlers.webview.postMessage(message);
    } else if (globalThis.Android && typeof globalThis.Android.postMessage === 'function') {
      // Android WebView
      globalThis.Android.postMessage(JSON.stringify(message));
    } else {
      // Fallback: use postMessage for testing or other webview implementations
      globalThis.parent.postMessage(message, this.config.allowedOrigin);
    }

    // If waiting for response, return a promise
    if (options.waitForResponse) {
      return new Promise<WebviewResponse<R>>((resolve, reject) => {
        const timeout = setTimeout(() => {
          this.pendingRequests.delete(messageId);
          reject(new Error(`Message timeout: ${type} (${options.timeout ?? 5000}ms)`));
        }, options.timeout ?? 5000);

        this.pendingRequests.set(messageId, {
          resolve: resolve as (value: WebviewResponse<Record<string, unknown>>) => void,
          reject,
          timeout,
        });
      });
    }
  }

  /**
   * Register a handler for a specific message type
   *
   * @param type - Message type to listen for
   * @param handler - Handler function
   * @param options - Listener options
   * @returns Function to unsubscribe the handler
   */
  public on<T = Record<string, unknown>>(
    type: string,
    handler: MessageHandler<T>,
    options: ListenerOptions = {}
  ): () => void {
    const messageType = `${this.config.messagePrefix}:${type}`;

    if (!this.handlers.has(messageType)) {
      this.handlers.set(messageType, new Set());
    }

    const handlerSet = this.handlers.get(messageType);
    if (!handlerSet) {
      return () => {
        // no-op
      };
    }

    // Wrap handler if once option is set
    let wrappedHandler: MessageHandler = handler as MessageHandler;
    if (options.once) {
      wrappedHandler = (message, direction) => {
        handlerSet.delete(wrappedHandler);
        if (handlerSet.size === 0) {
          this.handlers.delete(messageType);
        }
        (handler as MessageHandler)(message, direction);
      };
    }

    handlerSet.add(wrappedHandler);

    // Return unsubscribe function
    return () => {
      handlerSet.delete(wrappedHandler);
      if (handlerSet.size === 0) {
        this.handlers.delete(messageType);
      }
    };
  }

  /**
   * Register a one-time handler for a specific message type
   *
   * @param type - Message type to listen for
   * @param handler - Handler function
   * @returns Promise that resolves when message is received
   */
  public once<T = Record<string, unknown>>(type: string, handler?: MessageHandler<T>): Promise<WebviewMessage<T>> {
    return new Promise<WebviewMessage<T>>((resolve) => {
      const wrappedHandler: MessageHandler<T> = (message, direction) => {
        if (handler) {
          handler(message, direction);
        }
        resolve(message);
      };

      this.on(type, wrappedHandler, { once: true });
    });
  }

  /**
   * Remove all handlers for a specific message type
   */
  public off(type: string): void {
    const messageType = `${this.config.messagePrefix}:${type}`;
    this.handlers.delete(messageType);
  }

  /**
   * Remove all handlers
   */
  public removeAllListeners(): void {
    this.handlers.clear();
  }

  /**
   * Handle incoming messages from native webview
   */
  private handleIncomingMessage(event: MessageEvent): void {
    // Security check: verify origin if configured.
    // An empty `event.origin` is the conventional marker for messages coming
    // from the native webview container (Android/iOS/RN inject via
    // evaluateJavascript with no web origin attached), so we trust it.
    if (this.config.allowedOrigin !== '*' && event.origin && event.origin !== this.config.allowedOrigin) {
      return;
    }

    let rawMessage: unknown;

    try {
      rawMessage = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    } catch {
      return;
    }

    const message = this.normalizeIncomingMessage(rawMessage);

    if (!message) {
      return;
    }

    if (this.isResponseMessage(message)) {
      this.handleResponse(message);
      return;
    }

    this.handleMessage(message, 'from-native');
  }

  /**
   * Normalize incoming message by adding missing id and timestamp
   * Webview may only send type and data, so we auto-generate id and timestamp
   */
  private normalizeIncomingMessage(
    rawMessage: unknown
  ): WebviewMessage | WebviewResponse<Record<string, unknown>> | null {
    if (typeof rawMessage === 'object' && rawMessage !== null && 'type' in rawMessage && 'data' in rawMessage) {
      const msg = rawMessage as Record<string, unknown>;

      // If it's a response message
      if ('requestId' in msg && 'success' in msg) {
        return {
          requestId: coerceToString(msg.requestId) ?? this.generateMessageId(),
          data: msg.data as Record<string, unknown>,
          success: Boolean(msg.success),
          error: normalizeErrorField(msg.error),
          timestamp: msg.timestamp ? Number(msg.timestamp) : Date.now(),
        } as WebviewResponse<Record<string, unknown>>;
      }

      return {
        id: coerceToString(msg.id) ?? this.generateMessageId(),
        type: String(msg.type),
        data: msg.data,
        timestamp: msg.timestamp ? Number(msg.timestamp) : Date.now(),
      } as WebviewMessage;
    }

    return null;
  }

  /**
   * Handle a regular message by invoking registered handlers
   */
  private handleMessage(message: WebviewMessage, direction: MessageDirection): void {
    const handlers = this.handlers.get(message.type);
    if (!handlers || handlers.size === 0) {
      return;
    }

    handlers.forEach((handler) => {
      try {
        handler(message, direction);
      } catch {
        // Swallow handler errors so one bad handler cannot break the bridge
      }
    });
  }

  /**
   * Handle response message for pending requests
   */
  private handleResponse(response: WebviewResponse<Record<string, unknown>>): void {
    const pendingRequest = this.pendingRequests.get(response.requestId);
    if (!pendingRequest) {
      return;
    }

    clearTimeout(pendingRequest.timeout);
    this.pendingRequests.delete(response.requestId);

    if (response.success) {
      pendingRequest.resolve(response);
    } else {
      pendingRequest.reject(new Error(response.error ?? 'Unknown error'));
    }
  }

  /**
   * Check if message is a response message
   */
  private isResponseMessage(
    message: WebviewMessage | WebviewResponse<Record<string, unknown>>
  ): message is WebviewResponse<Record<string, unknown>> {
    return 'requestId' in message && 'success' in message;
  }

  /**
   * Generate unique message ID
   */
  private generateMessageId(): string {
    return globalThis.crypto.randomUUID();
  }
}

// Declare native webview bridges so they are visible on `globalThis`
/* eslint-disable vars-on-top, no-var */
declare global {
  var ReactNativeWebView: { postMessage: (message: string) => void } | undefined,
    webkit:
      | {
          messageHandlers?: {
            webview?: {
              postMessage: (message: unknown) => void;
            };
          };
        }
      | undefined,
    Android: { postMessage: (message: string) => void } | undefined;
}
/* eslint-enable vars-on-top, no-var */
